/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button } from 'antd';
import { CloseOutlined, GlobalOutlined } from '@ant-design/icons';
import AppLocale from '../../lngProvider';
import { switchLanguage } from '../../appRedux/actions';
import {
    getLanguageSuggestionDismissed,
    getStoredLocale,
    setLanguageSuggestionDismissed,
    setStoredLocale,
    setStoredStartPage
} from '../../util/uiPreferences';
import { browserLocale, chooseLocale, fetchUserPreferences, resolveStartPage } from '../../util/userPreferences';
import './PreferencesBootstrap.scss';

// Language names as written in the sentence's own language.
const NAMES = {
    en: {en: 'English', pl: 'Polish', es: 'Spanish'},
    pl: {en: 'angielski', pl: 'polski', es: 'hiszpański'},
    es: {en: 'inglés', pl: 'polaco', es: 'español'}
};
const BAR_TEXT = {
    en: (language) => `Your browser language is ${language}. Switch 2way to ${language}?`,
    pl: (language) => `Język Twojej przeglądarki to ${language}. Przełączyć 2way na ten język?`,
    es: (language) => `El idioma de tu navegador es ${language}. ¿Cambiar 2way a ${language}?`
};
const BUTTON_TEXT = {en: 'Switch to English', pl: 'Przełącz na polski', es: 'Cambiar a español'};
const CLOSE_TEXT = {en: 'Close', pl: 'Zamknij', es: 'Cerrar'};

// Runs once after login:
// - language: the user's own choice (this browser, else their account) wins; otherwise the account's
//   default language (set in admin); otherwise offer the browser's language in a bar they can close.
// - start page: the account copy refreshes the local cache; on the first visit on a device it also
//   moves the user from the default dashboard to their start page.
const PreferencesBootstrap = ({history, location, locale, switchLanguage, accountDefaultLanguage, accountLoaded}) => {
    const supported = Object.keys(AppLocale);
    const [prefs, setPrefs] = useState(undefined);
    const [suggestion, setSuggestion] = useState(null);

    useEffect(() => {
        fetchUserPreferences().then(data => {
            const result = data || {};
            setPrefs(result);
            if (result.startPage) {
                result.startPage = resolveStartPage(result.startPage);
                setStoredStartPage(result.startPage);
                let fromRoot = false;
                try {
                    fromRoot = window.sessionStorage.getItem('2way.rootRedirect') === '1';
                    window.sessionStorage.removeItem('2way.rootRedirect');
                } catch (e) {
                    // ignore
                }
                if (fromRoot && location.pathname === '/my-dashboard' && result.startPage !== '/my-dashboard') {
                    history.replace(result.startPage);
                }
            }
            if (!getStoredLocale() && result.locale && supported.includes(result.locale)) {
                setStoredLocale(result.locale);
                switchLanguage({locale: result.locale});
            }
        });
    }, []);

    useEffect(() => {
        if (prefs === undefined || !accountLoaded) return;
        const chosen = getStoredLocale() || prefs.locale;
        if (chosen) return;
        let current = locale?.locale || 'en';
        if (accountDefaultLanguage && supported.includes(accountDefaultLanguage) && accountDefaultLanguage !== current) {
            switchLanguage({locale: accountDefaultLanguage});
            current = accountDefaultLanguage;
        }
        const fromBrowser = browserLocale(supported);
        if (fromBrowser && fromBrowser !== current && getLanguageSuggestionDismissed() !== fromBrowser) {
            setSuggestion(fromBrowser);
        }
    }, [prefs, accountLoaded, accountDefaultLanguage]);

    if (!suggestion) return null;

    const current = locale?.locale && BAR_TEXT[locale.locale] ? locale.locale : 'en';
    const languageName = NAMES[current][suggestion] || suggestion;
    const dismiss = () => {
        setLanguageSuggestionDismissed(suggestion);
        setSuggestion(null);
    };

    return (
        <div className="language-bar" role="region" aria-label="Language">
            <GlobalOutlined className="language-bar__icon"/>
            <span className="language-bar__text">{BAR_TEXT[current](languageName)}</span>
            <Button
                size="small"
                className="language-bar__switch"
                onClick={() => {
                    chooseLocale(suggestion, switchLanguage);
                    setSuggestion(null);
                }}
            >
                {BUTTON_TEXT[suggestion] || languageName}
            </Button>
            <Button size="small" type="text" className="language-bar__close" icon={<CloseOutlined/>}
                    onClick={dismiss} aria-label={CLOSE_TEXT[current]}/>
        </div>
    );
};

const mapStateToProps = ({settings, accounts}) => ({
    locale: settings.locale,
    accountDefaultLanguage: accounts.defaultLanguage,
    accountLoaded: accounts.isLoadedPhoneNumbers
});

export default withRouter(connect(mapStateToProps, {switchLanguage})(PreferencesBootstrap));
